'use client'
import React, { useImperativeHandle, useRef, useState } from 'react'
import { Heading, Text } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { useExposure } from '../../hooks/useExposure.js'
import { useIntersectionObserverDelay } from '../../hooks/useIntersectionObserver.js'
import type { VideoFeatureProps } from './types.js'

const componentType = 'video'
const componentName = 'video_feature'

const VideoFeature = React.forwardRef<HTMLDivElement, VideoFeatureProps>(({ data, className }, ref) => {
  const { title, subtitle, poster, video, mobPoster, mobvideo } = data

  const boxRef = useRef<HTMLDivElement>(null)
  const desktopVideoRef = useRef<HTMLDivElement>(null)
  const mobileVideoRef = useRef<HTMLDivElement>(null)

  // 控制视频是否加载
  const [loadedDesktopVideoSrc, setLoadedDesktopVideoSrc] = useState('')
  const [loadedMobileVideoSrc, setLoadedMobileVideoSrc] = useState('')

  useExposure(boxRef, {
    componentType,
    componentName,
    componentTitle: title,
    componentDescription: subtitle,
  })

  useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

  // 桌面端视频懒加载
  useIntersectionObserverDelay(desktopVideoRef, {
    once: true,
    threshold: 0.1,
    callback: () => {
      setLoadedDesktopVideoSrc(video?.url)
    },
  })

  // 移动端视频懒加载
  useIntersectionObserverDelay(mobileVideoRef, {
    once: true,
    threshold: 0.1,
    callback: () => {
      if (mobvideo?.url) {
        setLoadedMobileVideoSrc(mobvideo?.url)
      }
    },
  })

  return (
    <section
      ref={boxRef}
      data-ui-component-id="VideoFeature"
      className={cn('video-feature rounded-box flex flex-col items-center text-white', className)}
    >
      <Heading as={'h2'} size={4} html={title} className="video-feature__title laptop:text-center w-full text-left" />
      <Text
        as={'p'}
        size={4}
        html={subtitle}
        className="video-feature__subtitle tablet:text-[14px] laptop:mt-[8px] laptop:text-center laptop:text-[14px] desktop:text-[16px] lg-desktop:text-[18px] mt-[4px] w-full text-left text-[14px]"
      />
      <div className="video-feature__video-wrapper rounded-box mt-[24px] overflow-hidden xl:h-[360px]">
        <div ref={desktopVideoRef} className="video-feature__desktop-video-container">
          <video
            playsInline
            autoPlay
            muted
            poster={poster?.url}
            src={loadedDesktopVideoSrc}
            loop
            className="video-feature__video video-feature__video--desktop tablet:block hidden size-full object-cover xl:h-[360px]"
          ></video>
        </div>
        <div ref={mobileVideoRef} className="video-feature__mobile-video-container">
          <video
            playsInline
            autoPlay
            muted
            poster={mobPoster?.url || ''}
            src={loadedMobileVideoSrc}
            loop
            className="video-feature__video video-feature__video--mobile tablet:hidden block"
          ></video>
        </div>
      </div>
    </section>
  )
})

VideoFeature.displayName = 'VideoFeature'

export default withLayout(VideoFeature)
